// =============================================================================
// VerifyEmailScreen — landing page for /verify-email/:token
// =============================================================================
// Public route (works signed-in or out). Consumes the token on mount and
// flips the account's verified state, then surfaces a "continue" link. If the
// signed-in user is in the store, we patch their emailVerified flag so the
// soft verify-banner disappears without a reload.
//
// Invalid/expired/already-used tokens collapse to one message — same posture
// as the password-reset screen.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../api';
import { useApp } from '../store';
import { Button, Window, Spinner } from '../components/ui';

type Status = { kind: 'loading' } | { kind: 'done' } | { kind: 'invalid' };

export function VerifyEmailScreen() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const user = useApp((s) => s.user);
  const setUser = useApp((s) => s.setUser);
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  // Guard against double-consume in React 18 StrictMode (effect runs twice in
  // dev). The token is single-use, so a second POST would report "invalid".
  const ran = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'invalid' });
      return;
    }
    if (ran.current) return;
    ran.current = true;
    api
      .verifyEmail(token)
      .then(() => {
        setStatus({ kind: 'done' });
        // Reflect immediately in the signed-in session, if any.
        const cur = useApp.getState().user;
        if (cur && !cur.emailVerified) setUser({ ...cur, emailVerified: true });
      })
      .catch(() => setStatus({ kind: 'invalid' }));
  }, [token, setUser]);

  return (
    <div className="min-h-full flex items-center justify-center p-6 bg-bg">
      <div className="w-full max-w-[380px]">
        <Window title="Verify email">
          {status.kind === 'loading' && (
            <div className="flex items-center justify-center p-6">
              <Spinner size={18} />
            </div>
          )}
          {status.kind === 'done' && (
            <div className="p-2 space-y-3 text-center">
              <div className="text-2xl">✓</div>
              <p className="text-sm text-text">Your email is verified. Thanks!</p>
              <Button onClick={() => navigate('/')} fullWidth variant="primary">
                {user ? 'Continue' : 'Sign in'}
              </Button>
            </div>
          )}
          {status.kind === 'invalid' && (
            <div className="p-2 space-y-3">
              <p className="text-sm text-text">
                This verification link is invalid, expired, or has already been used.
              </p>
              <p className="text-xs text-text-muted">
                {user
                  ? 'You can request a fresh link from the banner in the app.'
                  : 'Sign in and request a fresh link from the banner in the app.'}
              </p>
              <Button onClick={() => navigate('/')} fullWidth>
                {user ? 'Continue' : 'Back to sign in'}
              </Button>
            </div>
          )}
        </Window>
      </div>
    </div>
  );
}
